import Immutable from 'immutable';

import {
	webapi,
	createModuleState,
	EntitySearchAndDetailModel,
	DefinitionUtils,
	EntityDetailModel,
	styled,
	FormDefinition as Form,
	FormItemDefinition as FormItem,
	CrossDefinition as Cross,
	CentererDefinition as Centerer,
	MessageDefinition as Message,
	ButtonDefinition as Button,
	DateUtils,
	REPORTED_EXCEPTION,
	updateEntityTimestamp,
	showMessageBox,
	ValueValidator,
	ContextValidator,
	Icon,
	getLookup,
} from 'ps-gui';

import { getColumnText, getPhrase } from '../data/phrases';
import {
	createSearchModel,
	normalizePhoneNumber
} from './utils';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('UserAdmin');
const EMPTY_LIST = Immutable.List([]);

const formatDate = DateUtils.formatDate;

export default class UserAdminModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#setInitialized() {
		return new UserAdminModel(this.#data.set('initialized', true));
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new UserAdminModel(this.#data.setIn(name, value));
		} else {
			return new UserAdminModel(this.#data.set(name, value));
		}
	}

	initialize(globalContext) {
		let srch = createSearchModel('/api/dq/sv_native_user');
		srch = srch.setOutput([
			'user_name',
			'display_name',
			'd$from_date',
			'd$to_date',
			'email',
			'phone',
			'language',
			'activated',
			'is_system_admin',
			'is_submission_admin',
			'parties'
		]);
		srch = srch.setProcessFilter(filter => {
			if (filter.parties) {
				filter.party_id = filter.parties;
			}
			return filter;
		});
		return this.setEntitySearchAndDetail(
			new EntitySearchAndDetailModel()
				.setSearch(srch)
				.setDetail(getDetailModel())
				.setNewRowMode(EntitySearchAndDetailModel.NEW_ROW_MODE_LOAD_NULL_ID)
				.setEntityName('user')
		).#setProperty('langId', globalContext.getLanguageId()).#setInitialized();
	}

	async checkGlobalContextChange(globalContext) {
		if (!globalContext) {
			return this;
		}
		const gcLangId = globalContext.getLanguageId();
		const langId = this.#data.get('langId');
		if (gcLangId === langId) {
			return this;
		}
		let model = this;
		if (model.#data.get('initialized')) {
			// updateEntityTimestamp('user');
			model = model.#setProperty('langId', gcLangId);
		} else {
			model = model.initialize(globalContext);
		}
		return model.#setProperty('languageTimestamp', new Date().getTime());
	}

	setEntitySearchAndDetail(model) {
		return new UserAdminModel(this.#data.set('esad', model.getState()));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

let detailDefinition = Centerer(Form().setOpacity(ctx => ctx.getProperty('isDeleted') === 'Y' ? '0.3' : '1').setVirtual(false).setBasePath('detail').setItems([
	Cross().setItems([
		FormItem('userName', 'text')
			.setMandatory(true)
			.setMaxLength(200)
			// .setReadOnly(ctx => ctx.getProperty('id'))
			.setValidator(ValueValidator(value => {
				if (value && value.replace(/\s/g, '') !== value) {
					return getPhrase('userNameMustNotContainSpaces');
				} else {
					return null;
				}
			}))
			.onChange((ctx, me, value) => {
				if (value && value.indexOf('@') > 0) {
					ctx.setProperty('email', value);
				}
			}),
		FormItem('displayName', 'text').setMandatory(true)
	]),
	Cross().setItems([
		FormItem('fromDate', 'date').setMandatory(true).setWithTime(true).setReadOnly(true),
		FormItem('toDate', 'date').setWithTime(true).setReadOnly(true),
	]).setVisible(ctx => ctx.getProperty('id')),
	Cross().setItems([
		Button('terminate', getPhrase('terminate')).setListener(async e => {
			const ctx = e.ctx;
			const res = await webapi.execute('/api/proc/api_user/terminateUser', {
				id: ctx.getProperty('id')
			});
			if (res === REPORTED_EXCEPTION) {
				return;
			}
			updateEntityTimestamp('user');
			ctx.setModel(await ctx.getModel().reload());
		}).setVisible(ctx => !ctx.getProperty('toDate') && ctx.getProperty('id')).setDisabled(ctx => ctx.getRootProperty('detail') !== ctx.getRootProperty('originalDetail')),
		Button('reactivate', getPhrase('reactivate')).setListener(async e => {
			const ctx = e.ctx;
			const res = await webapi.execute('/api/proc/api_user/reactivateUser', {
				id: ctx.getProperty('id')
			});
			if (res === REPORTED_EXCEPTION) {
				return;
			}
			updateEntityTimestamp('user');
			ctx.setModel(await ctx.getModel().reload());
		}).setVisible(ctx => ctx.getProperty('toDate') && ctx.getProperty('id')).setDisabled(ctx => ctx.getRootProperty('detail') !== ctx.getRootProperty('originalDetail')),
		Button('impersonate', getPhrase('loginAsUser')).setListener(async e => {
			const ctx = e.ctx;
			const res = await webapi.execute('/api/proc/api_user/impersonateUser', {
				userId: ctx.getProperty('id')
			});
			if (res === REPORTED_EXCEPTION) {
				return;
			}
			const url = window.basePath + '?s=' + res.token;
			window.open(url, '_blank', 'noreferrer,noopener');
		}).setType('light').setVisible(ctx => !ctx.getProperty('toDate') && ctx.getProperty('id')).setDisabled(ctx => ctx.getRootProperty('detail') !== ctx.getRootProperty('originalDetail')),
	]),
	Cross().setItems([
		FormItem('phone', 'text')
			.setMandatory(true)
			.onChange((ctx, me, value) => {
				const normalized = normalizePhoneNumber(value);
				if (normalized && normalized !== value) {
					ctx.setProperty('phone', normalized);
				}
			}),
		FormItem('email', 'text').setMandatory(true),
		FormItem('languageId', 'lookup').setLookup('language')
	]),
	Cross().setItems([
		FormItem('isSystemAdmin', 'lookup').setLookup('ynBoolean').setMandatory(true),
		FormItem('isSubmissionAdmin', 'lookup').setLookup('ynBoolean').setMandatory(true),
	]).setValidator(ContextValidator((errors, ctx, itemName) => {
		let itemNames = itemName ? [itemName] : ['isSystemAdmin', 'isSubmissionAdmin'];
		const email = (ctx.getProperty('email') || '').toLowerCase();
		const canBeAdmin = email.endsWith('@raventia.cz') || email.endsWith('@intergram.cz');
		itemNames.forEach(name => {
			const value = ctx.getProperty(name);
			errors.add(
				ctx.getFullPath(name),
				value === 'Y' && !canBeAdmin ? getPhrase('onlyIntergramAndRaventiaUsersCanBeAdmins') : null,
				'cantBeAdmin'
			);
		});
	})),
	Cross().setItems([FormItem('comments', 'text').setMultiline(true)]),
	Cross().setItems([FormItem('parties', 'lookupList').setLookup('partyIdName')]),
	Message(ctx => {
		const bis = ctx.getProperty('bankIdentityStats');
		if (!bis) {
			return null;
		}
		const activeCount = bis.get('activeCount');
		const totalCount = bis.get('totalCount');
		if (activeCount > 0) {
			return (
				<ActivationMessage>{getPhrase('profileIsLinkedToBankIdentity')}</ActivationMessage>
			);
		}
		if (totalCount > 0) {
			return (
				<ActivationMessage>{getPhrase('profileWasLinkedToBankIdentity')}</ActivationMessage>
			);
		}
		return null;
	}),
	Cross().setItems([Button('sendActivationLink', getPhrase('sendActivationLink')).setListener(async e => {
		const ctx = e.ctx;
		const dlgRes = await showMessageBox(getPhrase('reallySendActivationLink'), {
			type: 'confirm'
		});
		if (dlgRes !== 'yes') {
			return;
		}
		const res = await webapi.execute('/api/user/createActivationAuthenticationCode', {
			userId: ctx.getProperty('id'),
			languageId: ctx.getProperty('languageId')
		});
		if (res === REPORTED_EXCEPTION) {
			return;
		}
		await showMessageBox(getPhrase('activationLinkSent'));
		ctx.setModel(await ctx.getModel().reload());
		updateEntityTimestamp('authenticationCode');
	}).setVisible(ctx => ctx.getProperty('id') && !ctx.getProperty('toDate') && !ctx.getProperty(['activation', 'activatedWhen']))]).setJustifyContent('right'),
	Message(ctx => {
		const activatedWhen = ctx.getProperty(['activation', 'activatedWhen']);
		const unusedActivationFrom = ctx.getProperty(['activation', 'unusedActivationFrom']);
		console.log('phone = ', ctx.getProperty('phone'));
		if (activatedWhen) {
			return (
				<ActivationMessage>{getPhrase('userActivated').replace('$when', DateUtils.formatDate(activatedWhen))}</ActivationMessage>
			);
		}
		if (unusedActivationFrom) {
			return (
				<ActivationMessage>{getPhrase('userActivationSent').replace('$when', DateUtils.formatDate(unusedActivationFrom))}</ActivationMessage>
			);
		}
		return null;
	}),
	Message(ctx => {
		const userId = ctx.getProperty('id');
		if (!userId) {
			return null;
		}
		const expanded = !!ctx.getRootProperty(['tmp', 'historyExpanded']);
		const rows = ctx.getRootProperty(['tmp', 'history']) || EMPTY_LIST;
		return (
			<ChangeHistory>
				<HistoryLink
					onMouseDown={async () => {
						ctx.setRootProperty(['tmp', 'historyExpanded'], !expanded);
						if (!expanded && !ctx.getRootProperty(['tmp', 'history'])) {
							let history = await webapi.execute('/api/proc/api_user/getUserHistory', {
								userId
							});
							if (history === REPORTED_EXCEPTION) {
								ctx.setRootProperty(['tmp', 'historyExpanded'], expanded);
								return;
							}
							ctx.setRootProperty(['tmp', 'history'], Immutable.fromJS(processUserHistory(history)));
						}
					}}
				>
					<Icon
						name={expanded ? 'expanded' : 'collapsed'}
					/>
					{getPhrase('changeHistory')}
				</HistoryLink>
				{
					expanded && rows.size === 0 && (
						<div className='body'>
							<div className='noHistory'>{getPhrase('noUserHistoryMessage')}</div>
						</div>
					)
				}
				{
					expanded && rows.map((row, index) => {
						const type = row.get('type');
						if (type === 'AP') {
							return (
								<div className="row" key={index}>
									<div className="header">
										<span className='type'>{getPhrase('userPartyAdded')}</span>
										<span className='whoen'>
											(
											<span className="label">{getColumnText('added_when')}:</span>
											<span className='value'>{formatDate(row.get('aud_when'))}</span>
											<span className="label">{getColumnText('added_by')}:</span>
											<span className='value'>{row.get('aud_who') || '???'}</span>
											)
										</span>
									</div>
									<div className='body'>
										<span className='party'>{row.get('party_name')} <span className='id'>({row.get('party_id')})</span></span>
									</div>
								</div>
							);
						} else if (type === 'DP') {
							return (
								<div className="row" key={index}>
									<div className="header">
										<span className='type'>{getPhrase('userPartyDeleted')}</span>
										<span className='whoen'>
											(
											<span className="label">{getColumnText('deleted_when')}:</span>
											<span className='value'>{formatDate(row.get('aud_when'))}</span>
											<span className="label">{getColumnText('deleted_by')}:</span>
											<span className='value'>{row.get('aud_who') || '???'}</span>
											)
										</span>
										<div className='body'>
											<span className='party'>{row.get('party_name')} <span className='id'>({row.get('party_id')})</span></span>
										</div>
									</div>
								</div>
							);
						} else if (type === 'UU') {
							return (
								<div className="row" key={index}>
									<div className="header">
										<span className='type'>{getPhrase('userModification')}</span>
										<span className='whoen'>
											(
											<span className="label">{getColumnText('edited_when')}:</span>
											<span className='value'>{formatDate(row.get('aud_when'))}</span>
											<span className="label">{getColumnText('edited_by')}:</span>
											<span className='value'>{row.get('aud_who') || '???'}</span>
											)
										</span>
										<div className='body'>
											<table className='userModification'>
												<tbody>
													{
														row.get('changes').map((c, index) => {
															return (
																<tr key={index}>
																	<td className='attr'>{getColumnText(c.get('attr'), 'user')}:</td>
																	<td className='oldValue'>{c.get('oldValue')}</td>
																	<td className='newValue'>{c.get('newValue')}</td>
																</tr>
															);
														})
													}
												</tbody>
											</table>
										</div>
									</div>
								</div>
							);
						}
					})
				}
			</ChangeHistory>
		);
	})
])).setWidth('96%').setMaxWidth('1000px');



const USER_HISTORY_ATTRIBUTES = [
	'user_id','user_name','display_name','from_date','to_date','email','phone','comments','is_system_admin','is_submission_admin', 'language_id'
];

function processUserHistory(history) {
	let rows = [];
	if (history.userParty) {
		rows = rows.concat(history.userParty);
	}
	const user = history.user;
	if (user && user.length > 1) {
		// {type: "AP", aud_when: "20241123010941", aud_who: "h$kpi", party_id: 1522}
		for (let i=1; i<user.length; i++) {
			const userRow = user[i];
			const nextUserRow = user[i - 1];
			let row = {
				type: 'UU',
				aud_when: userRow.aud_when,
				aud_who: userRow.aud_who,
				changes: USER_HISTORY_ATTRIBUTES.map(attr => {
					let oldValue = userRow[attr];
					let newValue = nextUserRow[attr];
					if (oldValue == newValue) {
						return null;
					}
					if (attr.endsWith('_date')) {
						newValue = formatDate(newValue);
						oldValue = formatDate(oldValue);
					} else if (attr.startsWith('is_')) {
						newValue = newValue === 'Y' ? getPhrase('yes') : newValue === 'N' ? getPhrase('no') : null;
						oldValue = oldValue === 'Y' ? getPhrase('yes') : newValue === 'N' ? getPhrase('no') : null;
					} else if (attr === 'language_id') {
						const lu = getLookup('language');
						newValue = lu.getText(newValue);
						oldValue = lu.getText(oldValue);
					}
					return {
						attr,
						oldValue,
						newValue
					};
				}).filter(Boolean)
			};
			if (row.changes.length > 0) {
				rows.push(row);
			}
		}
	}
	rows.sort((b, a) => {
		const av = a.aud_when || '';
		const bv = b.aud_when || '';
		if (av < bv) {
			return -1;
		}
		if (av > bv) {
			return 1;
		}
		return 0;
	});
	console.log('rows = ', rows);
	return rows;
}

const ChangeHistory = styled.div`
	padding-top: 8px;
	.noHistory {
		margin: 16px;
	}
	.row {
		margin: 16px;
		.header {
			.type {
				margin-right: 8px;
			}
			.whoen {
				font-size: 80%;
				opacity: 0.7;
				.label {
					margin-right: 4px;
					margin-left: 1px;
				}
				.value {
					margin-right: 1px;
					&:not(:last-of-type) {
						margin-right: 8px;
					}
					font-weight: 700;
				}
			}
		}
		.body {
			padding: 8px 16px;
			color: ${props => props.theme.disabledTextColor};
			.party {
				&:hover {
					background: ${props => props.theme.tableRowHoverBackground};
				}
				padding: 4px 8px;
				font-size: 14px;
				.id {
					font-weight: 500;
					font-size: 80%;
				}
			}
			.userModification {
				border-collapse: collapse;
				tr {
					&:hover {
						background: ${props => props.theme.tableRowHoverBackground};
					}
					td {
						padding: 4px 8px;
						font-size: 14px;
						&.attr {
							font-weight: 500;
							opacity: 0.7;
						}
						&.oldValue {
							text-decoration: line-through;
							opacity: 0.5;
						}
					}
				}
			}
		}
	}
`;

const HistoryLink = styled.div`
	color: ${props => props.theme.activeColor};
	font-weight: 500;
	cursor: pointer;
	&:hover {
		text-decoration: underline;
	}
	.icon {
		position: relative;
		top: -1px;
		margin-right: 4px;
	}
`;

const ActivationMessage = styled.div`
	color: ${props => props.theme.lightButtonColor};
	font-weight: 500;

	margin: 8px;
	text-align: right;
`;

let detailModel = null;

export function getDetailModel() {
	if (!detailModel) {
		DefinitionUtils.setLabels(detailDefinition);

		detailModel = new EntityDetailModel()
			.setLoadUri('/api/proc/api_user/getUserDetail')
			.setSaveUri('/api/proc/api_user/saveUser')
			.setDefinition(detailDefinition)
			.setEntityName('user');
	}
	return detailModel;
}

